import { useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconPlus, IconX, IconLayoutCards, IconUsers, IconSun, IconMoon, IconAperture, IconDroplet } from '@tabler/icons-react';
import { Text, Input, ButtonGroup, ErrorMsg } from '@fragments';
import Popper from '@fragments/Popper';
import { CommunityAssetImageField, AssetCategory } from '@features/uploadAsset';

// Labels are resolved at call time so they follow the active language; the
// `value` of each entry is the stored keyword and must never be translated.
const getRedirectChoices = (t) => [
    { value: 'community', label: t('builder.formConfig.redirect.community') },
    { value: 'link', label: t('builder.formConfig.redirect.link') },
];

const getThemeChoices = (t) => [
    { value: 'light', label: t('builder.formConfig.theme.light'), icon: <IconSun size={14} /> },
    { value: 'dark', label: t('builder.formConfig.theme.dark'), icon: <IconMoon size={14} /> },
];

function ActionCard({ action, removeAction }) {
    const { t } = useTranslation('formsModals');

    return (
        <div className="fbrp-action-card">
            <div className="fbrp-action-card-header">
                <div className="fbrp-action-card-type">
                    <IconUsers size={14} />
                    <Text.span contrast={6}>{t('builder.formConfig.actions.subscribeCommunity')}</Text.span>
                </div>
                <button className="fbrp-q-delete" onClick={() => removeAction(action.id)}>
                    <IconX size={13} />
                </button>
            </div>
        </div>
    );
}

export default function FormConfig({
    communityId,
    formName, setFormName,
    formActive, setFormActive,
    redirectChoice, setRedirectChoice,
    allowMultipleAnswers, setAllowMultipleAnswers,
    returnUrl, setReturnUrl,
    banner, setBanner,
    theme, setTheme,
    actions, addAction, removeAction,
    formErrors, clearError,
    scrollSignal,
}) {
    const { t } = useTranslation('formsModals');
    const popperRef = useRef();
    const bodyRef = useRef(null);

    const redirectChoices = useMemo(() => getRedirectChoices(t), [t]);
    const themeChoices = useMemo(() => getThemeChoices(t), [t]);

    useEffect(() => {
        if (!scrollSignal || !bodyRef.current) return;
        const id = setTimeout(() => {
            const field = bodyRef.current?.querySelector('[data-field-error="true"]');
            if (!field) return;
            field.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            field.querySelector('input, [contenteditable="true"]')?.focus();
        }, 80);
        return () => clearTimeout(id);
    }, [scrollSignal]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRedirectChoice = (choice) => {
        setRedirectChoice(choice);
        if (choice === 'community') {
            setReturnUrl(null);
            clearError?.(['form', 'returnUrl']);
        }
    };

    return (
        <div className="form-builder-right-panel">
            <div className="fbrp-header">
                <div className="fbrp-header-left">
                    <div className="fbrp-form-icon">
                        <IconLayoutCards size={15} />
                    </div>
                    <Text.span contrast={8} weight={600}>{t('common:entities.form')}</Text.span>
                </div>
            </div>

            <div className="fbrp-body" ref={bodyRef}>
                <div className="fbrp-section">
                    <div className="fbrp-section-label">
                        <Text.span contrast={4}>{t('builder.formConfig.sections.general')}</Text.span>
                    </div>

                    <div className="fbrp-field" data-field-error={formErrors?.formName ? 'true' : undefined}>
                        <Input
                            value={formName}
                            onInput={v => {
                                setFormName(v);
                                clearError?.(['form', 'formName']);
                            }}
                            label={t('builder.formConfig.formNameLabel')}
                            placeholder={t('builder.defaults.newFormName')}
                            fullWidth
                            error={!!formErrors?.formName}
                        />
                        <ErrorMsg>{formErrors?.formName}</ErrorMsg>
                    </div>

                    <Input.Switch label={t('builder.formConfig.activeSwitch')} value={formActive} onInput={val => setFormActive(val)} />
                    <Input.Switch label={t('builder.formConfig.allowMultipleAnswers')} value={allowMultipleAnswers} onInput={val => setAllowMultipleAnswers(val)} />

                    <ErrorMsg>{formErrors?.steps}</ErrorMsg>
                </div>

                <div className="fbrp-section">
                    <div className="fbrp-section-label">
                        <Text.span contrast={4}>{t('builder.formConfig.sections.appearance')}</Text.span>
                    </div>

                    <Text.h4
                        className="mt-2 mb-1"
                        contrast={6}
                        weight={400}
                        left={<IconAperture />}
                    >
                        {t('builder.formConfig.bannerTitle')}
                    </Text.h4>

                    <CommunityAssetImageField
                        communityId={communityId}
                        category={AssetCategory.FORM_BANNER}
                        value={banner}
                        onChange={setBanner}
                        aspectRatio={5 / 3}
                        title={t('builder.formConfig.bannerFieldTitle')}
                        fullWidth
                    />

                    <Text.h4
                        className="mt-3 mb-1"
                        contrast={6}
                        weight={400}
                        left={<IconDroplet />}
                    >
                        {t('builder.formConfig.themeTitle')}
                    </Text.h4>
                    <ButtonGroup
                        options={themeChoices}
                        value={theme}
                        onChange={setTheme}
                        size="small"
                    />
                </div>

                <div className="fbrp-section">
                    <div className="fbrp-section-label">
                        <Text.span contrast={4}>{t('builder.formConfig.sections.postSubmitActions')}</Text.span>
                    </div>

                    <Input.Choices
                        value={redirectChoice}
                        onInput={handleRedirectChoice}
                        options={redirectChoices}
                    />

                    {redirectChoice === 'link' && (
                        <div className="fbrp-field" style={{ marginTop: 'var(--gap-1)' }} data-field-error={formErrors?.returnUrl ? 'true' : undefined}>
                            <Input
                                value={returnUrl ?? ''}
                                onInput={val => {
                                    setReturnUrl(val || null);
                                    clearError?.(['form', 'returnUrl']);
                                }}
                                label={t('builder.formConfig.returnUrlLabel')}
                                placeholder="https://..."
                                fullWidth
                                error={!!formErrors?.returnUrl}
                            />
                            <ErrorMsg>{formErrors?.returnUrl}</ErrorMsg>
                        </div>
                    )}

                    {actions.length > 0 && (
                        <div className="fbrp-action-cards" style={{ marginTop: 'var(--gap-1)' }}>
                            {actions.map(action => (
                                <ActionCard key={action.id} action={action} removeAction={removeAction} />
                            ))}
                        </div>
                    )}

                    <button
                        className="fbrp-add-btn"
                        style={{ marginTop: 'var(--gap-1)' }}
                        onClick={(e) => popperRef.current.open(
                            [{ id: 'subscribe_community', label: t('builder.formConfig.actions.subscribeCommunity'), icon: <IconUsers size={16} />, onClick: () => addAction('subscribe_community') }],
                            'top-start',
                            e.currentTarget,
                        )}
                    >
                        <IconPlus size={13} />
                        <Text.span contrast={5}>{t('builder.formConfig.addActionButton')}</Text.span>
                    </button>
                </div>
            </div>

            <Popper controlRef={popperRef} />
        </div>
    );
}
